import { useParams, useNavigate } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Badge from "../../components/common/Badge";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import { getFullImageUrl } from "../../utils/imageUrl";
import { getCatalogOwnershipMessage } from "../../utils/catalogOwnership";
import {
  useGetItemsQuery,
  useGetCategoriesQuery,
  // useDeleteItemMutation,
  useGetCatalogReferenceQuery,
} from "../../store/api";
import { useState } from "react";

const ItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { data: itemsData, isLoading, isError, error } = useGetItemsQuery();
  const { data: categoriesData } = useGetCategoriesQuery();
  const { data: referenceData } = useGetCatalogReferenceQuery();
  // const [deleteItem] = useDeleteItemMutation();

  const [deleteModal, setDeleteModal] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const items = Array.isArray(itemsData) ? itemsData : [];
  const categories = Array.isArray(categoriesData) ? categoriesData : [];
  const proteinTypes = Array.isArray(referenceData?.proteinTypes)
    ? referenceData.proteinTypes
    : [];
  const item = items.find((i) => i.id === itemId);
  const category = item
    ? categories.find((c) => c.id === item.categoryId)
    : null;
  const proteinTypeNames = (item?.proteinTypeIds || [])
    .map((id) => proteinTypes.find((type) => type.id === id)?.name)
    .filter(Boolean);

  const handleDelete = async () => {
    try {
      // Deprecated in Edge: item delete is managed in Central Admin.
      // await deleteItem(itemId).unwrap();
      // navigate("/menu/items");
    } catch (err) {
      setDeleteModal(false);
      const errorMessage =
        err?.data?.detail || err?.data?.message || "Failed to delete item.";
      setErrorModal({ isOpen: true, message: errorMessage });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Loading..." />
        <Card>
          <p className="text-sm text-slate-500">Loading item details...</p>
        </Card>
      </div>
    );
  }

  if (isError || !item) {
    return (
      <div className="space-y-6">
        <PageHeader title="Item Not Found" />
        <Card>
          <Alert tone="error">
            {isError
              ? getCatalogOwnershipMessage(
                  error,
                  "Unable to load item details from Edge catalog sync. Check Central Admin changes and sync status.",
                )
              : "The item you're looking for doesn't exist in synced Edge data."}
          </Alert>
          <div className="mt-4">
            <Button onClick={() => navigate("/menu/items")}>
              Back to Items
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const dietaryInfo = [
    { key: "isVegetarian", label: "Vegetarian", value: item.isVegetarian },
    { key: "isVegan", label: "Vegan", value: item.isVegan },
    { key: "isHalal", label: "Halal", value: item.isHalal },
    { key: "isKosher", label: "Kosher", value: item.isKosher },
    {
      key: "containsAlcohol",
      label: "Contains Alcohol",
      value: item.containsAlcohol,
    },
    { key: "isSpicy", label: "Spicy", value: item.isSpicy },
  ].filter((info) => info.value);

  return (
    <div className="space-y-6">
      <PageHeader
        title={item.name}
        breadcrumbs={[
          { label: "Menu Items", path: "/menu/items" },
          { label: item.name },
        ]}
        actions={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate("/menu/items")}
            >
              Back to List
            </Button>
            {/* Deprecated in Edge: item edit/delete actions */}
            {/*
            <Button size="sm" variant="secondary" onClick={handleEdit}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => setDeleteModal(true)}
            >
              Delete
            </Button>
            */}
          </div>
        }
      />

      <Card>
        <div className="grid gap-6 lg:grid-cols-[240px_minmax(0,1fr)]">
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-800">
            <div className="flex justify-center">
              {item.imageUrl ? (
                <img
                  src={getFullImageUrl(item.imageUrl)}
                  alt={item.name}
                  className="h-52 w-full max-w-[208px] rounded-xl border border-slate-200 object-cover shadow-sm dark:border-slate-700"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className="flex h-52 w-full max-w-[208px] items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"
                style={{ display: item.imageUrl ? "none" : "flex" }}
              >
                <svg
                  className="h-12 w-12 text-slate-400 dark:text-slate-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

          </div>

          <div className="space-y-6">
            {item.description && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Description
                </h3>
                <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                  {item.description}
                </p>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/70">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Price
                </span>
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  ${parseFloat(item.basePrice || 0).toFixed(2)}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/70">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Category
                </span>
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {category?.name || "—"}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-800/70">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Status
                </span>
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {item.isAvailable ? "Available" : "Unavailable"}
                </div>
              </div>
            </div>

            {dietaryInfo.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Dietary Information
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {dietaryInfo.map((info) => (
                    <Badge key={info.key} tone="info">
                      {info.label}
                      {info.key === "isSpicy" && item.spiceLevel > 0 && (
                        <span className="ml-1">({item.spiceLevel}/5)</span>
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {(proteinTypeNames.length > 0 ||
              (item.allergens && item.allergens.length > 0) ||
              (item.modifierGroups && item.modifierGroups.length > 0)) && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Item Metadata
                </span>
                <div className="space-y-3">
                  {proteinTypeNames.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        Protein Types
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {proteinTypeNames.map((name) => (
                          <Badge key={name} tone="info">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {item.allergens && item.allergens.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        Allergens
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {item.allergens.map((allergen) => (
                          <Badge
                            key={allergen.allergenId || allergen.id}
                            tone="info"
                          >
                            {allergen.name || allergen.code || "Allergen"}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {item.modifierGroups && item.modifierGroups.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                        Modifier Groups
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {item.modifierGroups.map((group) => (
                          <Badge
                            key={group.groupId || group.id}
                            tone="info"
                          >
                            {group.name || "Modifier Group"}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Deprecated in Edge: item delete modal */}
      {false && (
        <Modal
          isOpen={deleteModal}
          onClose={() => setDeleteModal(false)}
          title="Delete Item"
          confirmText="Delete"
          confirmTone="danger"
          onConfirm={handleDelete}
        >
          <p>
            Are you sure you want to delete <strong>{item.name}</strong>?
          </p>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            This action cannot be undone.
          </p>
        </Modal>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
        title="Error"
        confirmText="OK"
        onConfirm={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <p>{errorModal.message}</p>
      </Modal>
    </div>
  );
};

export default ItemDetail;

